import { Link } from 'react-router-dom';
import { getTopArticles, getHotArticles, getLatestArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';

export default function Home() {
  const topArticles = getTopArticles();
  const hotArticles = getHotArticles();
  const latestArticles = getLatestArticles(8);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteInfo.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${siteInfo.name} - ${siteInfo.tagline}`,
    description: siteInfo.description,
    url: siteInfo.url,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOHead
        title={`${siteInfo.name} · ${siteInfo.tagline}`}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl={siteInfo.url}
        ogImage="https://readdy.ai/api/search-image?query=Breaking%20news%20entertainment%20gossip%20scandal%20headlines%20collage%2C%20dramatic%20red%20and%20black%20theme%2C%20Chinese%20entertainment%20news%20website%20hero%20image%2C%20bold%20typography%2C%20dark%20atmosphere&width=1200&height=630&seq=og-home&orientation=landscape"
        schema={{ ...websiteSchema, ...organizationSchema, ...webpageSchema }}
      />

      <main>
        {/* Hero Section */}
        <section className="relative bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950 via-foreground-950/95 to-foreground-950"></div>
          <div className="relative px-4 md:px-6 py-12 md:py-16 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Top Story - Hero */}
              {topArticles[0] && (
                <Link
                  to={`/article/${topArticles[0].id}`}
                  className="lg:col-span-2 group relative overflow-hidden rounded-xl"
                >
                  <div className="aspect-[16/9] lg:aspect-[21/9] overflow-hidden rounded-xl">
                    <img
                      src={topArticles[0].imageUrl}
                      alt={topArticles[0].title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/20 to-transparent rounded-xl"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="inline-block text-xs px-3 py-1 rounded-full bg-accent-500 text-background-50 font-medium mb-3 whitespace-nowrap">
                      头条爆料
                    </span>
                    <h1 className="text-xl md:text-3xl font-bold text-background-50 group-hover:text-accent-500 transition-colors mb-2 line-clamp-2">
                      {topArticles[0].title}
                    </h1>
                    <p className="text-sm text-background-50/70 line-clamp-2 hidden sm:block">
                      {topArticles[0].excerpt}
                    </p>
                    <div className="flex items-center gap-3 mt-3 text-xs text-background-50/50">
                      <span>{topArticles[0].publishDate}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {(topArticles[0].views / 10000).toFixed(1)}万阅读
                      </span>
                    </div>
                  </div>
                </Link>
              )}

              {/* Side hot list */}
              <div className="bg-foreground-900/80 rounded-xl p-5 border border-foreground-800">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-fire-line text-accent-500 text-lg"></i>
                  <h2 className="text-background-50 font-semibold text-sm">24小时热榜</h2>
                </div>
                <div className="space-y-0">
                  {hotArticles.slice(0, 6).map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="flex items-start gap-3 py-3 border-b border-foreground-800 last:border-0 group"
                    >
                      <span className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-xs font-bold ${
                        index < 3 ? 'bg-accent-500 text-background-50' : 'bg-foreground-800 text-background-50/50'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm text-background-50/90 group-hover:text-accent-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <span className="text-xs text-background-50/40 mt-1 block">
                          {(article.views / 10000).toFixed(1)}万阅读
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="px-4 md:px-6 py-8 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-5">
            <i className="ri-grid-line text-accent-500"></i>
            <h2 className="text-lg font-semibold text-foreground-900">分类浏览</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-5 bg-background-50 rounded-lg border border-background-200 hover:border-accent-500/30 hover:bg-accent-500/5 transition-all group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10 group-hover:bg-accent-500/20 transition-colors">
                  <i className={`${cat.icon} text-accent-500 text-xl`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                <span className="text-xs text-foreground-400">{cat.count}篇</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Hot Articles Grid */}
        <section className="px-4 md:px-6 py-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <i className="ri-flashlight-line text-accent-500"></i>
                <h2 className="text-lg font-semibold text-foreground-900">热门爆料</h2>
              </div>
              <Link
                to="/category/bagua"
                className="text-sm text-accent-500 hover:text-accent-600 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {hotArticles.slice(0, 8).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="px-4 md:px-6 py-8 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <i className="ri-timer-line text-accent-500"></i>
              <h2 className="text-lg font-semibold text-foreground-900">最新爆料</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {latestArticles.map((article) => (
              <ArticleCard key={article.id} article={article} variant="horizontal" />
            ))}
          </div>
        </section>
      </main>
    </>
  );
}